/**
 * Slug ↔ route resolution for the authenticated shell.
 *
 * The navbar speaks in page SLUGS (`onNavigate('lotes')`) while the router
 * speaks in ROUTES (`/lots`), so both directions must agree: a slug the layout
 * does not know silently lands the user on `/transactions`, and a route the
 * layout does not know leaves every nav entry unhighlighted. Deriving both maps
 * from `AUTHENTICATED_MAIN_NAV_ITEMS` makes that impossible to get wrong.
 */

import { AUTHENTICATED_MAIN_NAV_ITEMS } from '@/shared/components/layout/mainNavItems'

/** Pages reachable from the user dropdown / auth flow, not from the main nav. */
const NON_NAV_ROUTES_BY_SLUG: Readonly<Record<string, string>> = {
  perfil: '/profile',
  soporte: '/support',
  clientes: '/workspaces',
  home: '/',
  login: '/login',
  // The org-admin's whole nav is this single entry, so an unregistered slug
  // here leaves their 16-page admin panel with no way in at all.
  'admin-org': '/admin/org/dashboard',
}

const ROUTES_BY_SLUG: Readonly<Record<string, string>> = {
  ...Object.fromEntries(AUTHENTICATED_MAIN_NAV_ITEMS.map((item) => [item.slug, item.route])),
  ...NON_NAV_ROUTES_BY_SLUG,
}

/** Where an unknown slug goes — the historical default of this shell. */
const DEFAULT_ROUTE = '/transactions'
const DEFAULT_PAGE_SLUG = 'transacciones'

/** Route for a navbar slug; unknown slugs fall back to the transactions page. */
export const resolveRouteForPageSlug = (slug: string): string =>
  ROUTES_BY_SLUG[slug] ?? DEFAULT_ROUTE

/**
 * Slug of the nav entry that must appear active for a pathname.
 *
 * Sub-routes highlight their parent entry (`/import/custom` → `importar`,
 * `/support/42` → `soporte`), which is why this is not a plain reverse lookup.
 */
export const resolvePageSlugForPath = (pathname: string): string => {
  const exactMatch = AUTHENTICATED_MAIN_NAV_ITEMS.find((item) => item.route === pathname)
  if (exactMatch) return exactMatch.slug

  // Prefixed sections: the feature owns every path below its route.
  const prefixMatch = AUTHENTICATED_MAIN_NAV_ITEMS.find((item) =>
    pathname.startsWith(`${item.route}/`),
  )
  if (prefixMatch) return prefixMatch.slug

  if (pathname === '/profile') return 'perfil'
  if (pathname.startsWith('/support')) return 'soporte'
  if (pathname.startsWith('/admin/org')) return 'admin-org'

  return DEFAULT_PAGE_SLUG
}
